import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LiliButton } from '@lili/lili-frontend-common';
import { GENERAL_STYLE, FORGOT_PASSWORD_FLOW } from '../constants';
import LoginCard from '../../../components/LoginCard';
import Heading from '../../../../../generic-components/Heading';
import { LoginHelpNavBar } from '../LoginHelpNavBar';
import { LiliPasswordInput } from '../../../../../DesignSystem/LiliPasswordInput/LiliPasswordInput';
import { usePasswordRecoveryPassword } from '../ForgotEmail/hooks/forgotPasswordHooks';
import { ForgotPasswordGeneralErrorModal } from '../Errors/ForgotPasswordGeneralErrorModal';
import { SERVER_ERRORS_DATA } from '../../../../../constants/serverErrors';
import { ReactComponent as ClockIcon } from '../../../../../assets/icons/svg/stamp-icons/ic-stamp-timewatch.svg';
import { Layout } from '../../../../../components/Layout/Layout';

interface IProps {
  customerId: string;
  token: string;
  onPasswordChanged: () => void;
  onClose: () => void;
  onBack: () => void;
  onTryAgainClick: () => void;
}

const ForgotPasswordNewPasswordStep = ({
  onPasswordChanged,
  onClose,
  onBack,
  token,
  customerId,
  onTryAgainClick,
}: IProps) => {
  const { t } = useTranslation();
  const [passwordInput, setPasswordInput] = useState<string>('');
  const [showError, setShowError] = useState<boolean>(false);
  const [inlineError, setInlineError] = useState<string>('');
  const [showTokenExpiredError, setShowTokenExpiredError] = useState<boolean>(false);

  const minimumChars = 8;
  const rulesArray = [
    t(FORGOT_PASSWORD_FLOW.NEW_PASSWORD.RULE_1),
    t(FORGOT_PASSWORD_FLOW.NEW_PASSWORD.RULE_2),
    t(FORGOT_PASSWORD_FLOW.NEW_PASSWORD.RULE_3),
  ];
  const {
    mutateAsync: mutatePasswordRecoveryPassword,
    isLoading: isChangingPassword,
  } = usePasswordRecoveryPassword(customerId, (code, msg) => {
    switch (code) {
      case SERVER_ERRORS_DATA.WRONG_PASSWORD_FORMAT:
      case SERVER_ERRORS_DATA.PASSWORD_TOO_WEAK:
      case SERVER_ERRORS_DATA.PASSWORD_TOO_LONG:
        setInlineError(msg);
        break;
      case SERVER_ERRORS_DATA.TOKEN_EXPIRED:
        setShowTokenExpiredError(true);
        setShowError(false);
        break;
      default:
        setShowError(true);
        setShowTokenExpiredError(false);
        break;
    }
  });
  const changePassword = () => {
    const body = {
      token,
      setPasswordData: { password: passwordInput },
    };

    mutatePasswordRecoveryPassword(body).then(() => {
      onPasswordChanged();
    });
  };
  const heading = (
    <Heading
      title={t(FORGOT_PASSWORD_FLOW.NEW_PASSWORD.TITLE)}
      className={`${GENERAL_STYLE.HEADING.CLASS_NAME} mb-[16px]`}
    />
  );
  const rules = (
    <div className='mb-[20px]'>
      {rulesArray.map((rule) => (
        <Heading
          title={`• ${rule}`}
          className={`${GENERAL_STYLE.DESCRIPTION.CLASS_NAME}`}
        />
      ))}
    </div>
  );

  const confirmButton = (
    <LiliButton
      id='mainButton'
      title={t(FORGOT_PASSWORD_FLOW.NEW_PASSWORD.CONFIRM_BUTTON_TITLE)}
      className={`${GENERAL_STYLE.MAIN_BUTTON.CLASS_NAME}`}
      disabled={passwordInput.length < minimumChars}
      isLoading={isChangingPassword}
      onClick={() => {
        setInlineError('');
        setShowError(false);
        changePassword();
      }}
    />
  );

  const passwordInputField = (
    <LiliPasswordInput
      id='password_text_field'
      title={t(FORGOT_PASSWORD_FLOW.NEW_PASSWORD.INPUT_TITLE)}
      onChange={(input: string) => setPasswordInput(input)}
      errorMessage={inlineError}
    />
  );
  const userLockedError = showTokenExpiredError && (
    <ForgotPasswordGeneralErrorModal
      title={t(FORGOT_PASSWORD_FLOW.ERRORS.TOKEN_EXPIRED_ERROR_TITLE)}
      description={t(
        FORGOT_PASSWORD_FLOW.ERRORS.TOKEN_EXPIRED_ERROR_DESCRIPTION,
      )}
      onAction={() => {
        setShowError(false);
        setShowTokenExpiredError(false);
        onTryAgainClick();
      }}
      hideSecondaryButton
      icon={<ClockIcon />}
      onClose={() => setShowTokenExpiredError(false)}
    />
  );
  const errorModal = showError && (
    <ForgotPasswordGeneralErrorModal
      title={t(FORGOT_PASSWORD_FLOW.ERRORS.GENERAL_ERROR_TITLE)}
      description={t(FORGOT_PASSWORD_FLOW.ERRORS.GENERAL_ERROR_DESCRIPTION)}
      onAction={() => setShowError(false)}
      onClose={() => {
        setShowError(false);
      }}
    />
  );

  return (
    <Layout shouldHideProfile shouldHideChatBot wrapperClassName='pt-[78px] pl-[72px] ml-[100px] pc-md:ml-[256px]'>
      <LoginHelpNavBar
        onBackClick={onBack}
        onCloseClick={onClose}
        hideBackIcon
      />
      <LoginCard className={GENERAL_STYLE.LOGIN_CARD.CLASS_NAME}>
        <div className='flex flex-col justify-between h-full'>
          <div className='mb-[62px]'>
            {heading}
            {rules}
            {passwordInputField}
            {userLockedError}
            {errorModal}
          </div>
          {confirmButton}
        </div>
      </LoginCard>
    </Layout>
  );
};

export default ForgotPasswordNewPasswordStep;
